import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "@mantine/form";
import {
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Select,
  Loader,
  Center,
  ActionIcon,
} from "@mantine/core";
import { IconDeviceFloppy, IconArrowLeft } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useStore } from "../store";
import { getPimSchemas } from "../Pim/pimApi";
import { getSharedSchemas } from "../Pim/vendorApi";
import { createMappingProfile } from "../api";

export default function MappingProfileForm() {
  const { t } = useTranslation("imports");
  const [, navigate] = useLocation();
  const profile = useStore((state) => state.profile);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [ownSchemaIds, setOwnSchemaIds] = useState(new Set());

  const form = useForm({
    initialValues: {
      name: "",
      sourceSchemaId: "",
      targetSchemaId: "",
    },
    validate: {
      name: (value) =>
        value.trim() ? null : t("mappings.profileForm.nameRequired"),
      sourceSchemaId: (value) =>
        value ? null : t("mappings.profileForm.sourceRequired"),
      targetSchemaId: (value) =>
        value ? null : t("mappings.profileForm.targetRequired"),
    },
  });

  useEffect(() => {
    const loadSchemas = async () => {
      try {
        setLoading(true);
        const [ownSchemas, shared] = await Promise.all([
          getPimSchemas(profile.tenantId),
          getSharedSchemas().catch(() => []),
        ]);
        setOwnSchemaIds(new Set(ownSchemas.map((s) => s.id)));
        setSchemas([...ownSchemas, ...(shared || [])]);
      } catch {
        notifications.show({
          color: "red",
          title: t("mappings.common.errorTitle"),
          message: t("mappings.profileForm.schemasLoadFailed"),
        });
      } finally {
        setLoading(false);
      }
    };
    loadSchemas();
  }, [profile.tenantId]);

  const handleCreate = async (values) => {
    try {
      setSaving(true);

      const newProfile = await createMappingProfile({
        name: values.name.trim(),
        sourceSchemaId: values.sourceSchemaId,
        targetSchemaId: values.targetSchemaId,
        rules: [],
        allowUnknown: true,
        allowUnmapped: true,
      });

      notifications.show({
        color: "green",
        title: t("mappings.common.successTitle"),
        message: t("mapToTarget.createdPlain"),
      });

      navigate(`/mapping-profiles/${newProfile.id}`);
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: error.message || t("mapToTarget.createFailedMessage"),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container size="sm">
        <Center pt="xl">
          <Loader size="xl" />
        </Center>
      </Container>
    );
  }

  const schemaOptions = schemas.map((s) => ({
    value: s.id,
    label: s.labels?.en || s.name,
    group: ownSchemaIds.has(s.id)
      ? t("mappings.profileForm.mySchemas")
      : t("mappings.profileForm.sharedSchemas"),
  }));

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Group>
          <ActionIcon
            variant="subtle"
            onClick={() => navigate("/mapping-profiles")}
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>{t("mappings.profileForm.title")}</Title>
        </Group>

        <Card withBorder shadow="sm" padding="lg">
          <form onSubmit={form.onSubmit(handleCreate)}>
            <Stack gap="md">
              <TextInput
                label={t("mappings.profile.profileName")}
                placeholder={t("mappings.profileForm.namePlaceholder")}
                required
                {...form.getInputProps("name")}
              />

              <Select
                label={t("mappings.profile.sourceSchema")}
                placeholder={t("mappings.profileForm.selectSource")}
                description={t("mappings.profileForm.sourceDescription")}
                data={schemaOptions}
                required
                searchable
                {...form.getInputProps("sourceSchemaId")}
              />

              <Select
                label={t("mappings.targetSchema")}
                placeholder={t("mappings.profileForm.selectTarget")}
                description={t("mappings.profileForm.targetDescription")}
                data={schemaOptions}
                required
                searchable
                {...form.getInputProps("targetSchemaId")}
              />

              <Text size="sm" c="dimmed">
                {t("mappings.profileForm.hint")}
              </Text>

              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={() => navigate("/mapping-profiles")}
                >
                  {t("common:generic.action_cancel")}
                </Button>
                <Button
                  type="submit"
                  leftSection={<IconDeviceFloppy size={18} />}
                  loading={saving}
                >
                  {t("mappings.profileForm.submit")}
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}
